import React, { useState, useCallback, useMemo, useEffect } from 'react';

/**
 * FileExclusionModal
 * 
 * Shows a project's folder/file tree after ZIP extraction or repo download.
 * Users can exclude entire folders or individual XAML files before processing.
 * 
 * Props:
 *   isOpen       - boolean
 *   onClose      - () => void
 *   onConfirm    - ({ excludedFiles: string[], excludeCommentOutActivities: boolean, removeArgumentDefaultValues: boolean }) => void  — proceeds with processing
 *   fileTree     - { name, type, children?, path } recursive tree from backend
 *   projectName  - string  (technical name or display name)
 *   totalFiles   - number  (total XAML count)
 *   loading      - boolean (while confirm is processing)
 */

/* ------------------------------------------------------------------ */
/*  Utility: count XAML files in a subtree                              */
/* ------------------------------------------------------------------ */
const countXamlFiles = (node) => {
  if (node.type === 'file') return 1;
  if (!node.children) return 0;
  return node.children.reduce((sum, c) => sum + countXamlFiles(c), 0);
};

/* Collect all file paths under a node */
const collectFilePaths = (node) => {
  if (node.type === 'file') return [node.path];
  if (!node.children) return [];
  return node.children.flatMap(collectFilePaths);
};

/* ------------------------------------------------------------------ */
/*  TreeNode – recursive, memoised                                      */
/* ------------------------------------------------------------------ */
const TreeNode = React.memo(({ node, depth, excluded, onToggle }) => {
  const [expanded, setExpanded] = useState(depth < 2); // auto-expand first 2 levels

  const isFile = node.type === 'file';
  const childPaths = useMemo(() => collectFilePaths(node), [node]);
  const excludedCount = useMemo(
    () => childPaths.filter((p) => excluded.has(p)).length,
    [childPaths, excluded]
  );
  const totalCount = childPaths.length;
  const isFullyExcluded = totalCount > 0 && excludedCount === totalCount;
  const isPartiallyExcluded = excludedCount > 0 && excludedCount < totalCount;

  const checked = isFile ? !excluded.has(node.path) : !isFullyExcluded;
  const indeterminate = !isFile && isPartiallyExcluded;

  const handleToggle = useCallback(() => {
    if (isFile) {
      onToggle([node.path], excluded.has(node.path) ? 'include' : 'exclude');
    } else {
      // Toggle all children
      onToggle(childPaths, isFullyExcluded ? 'include' : 'exclude');
    }
  }, [isFile, node.path, childPaths, isFullyExcluded, excluded, onToggle]);

  const checkboxRef = useCallback(
    (el) => {
      if (el) el.indeterminate = indeterminate;
    },
    [indeterminate]
  );

  const paddingLeft = depth * 20 + 8;

  return (
    <>
      <div
        className={`flex items-center py-1 px-2 rounded group select-none ${
          isFile && excluded.has(node.path) ? 'opacity-50' : ''
        }`}
        style={{ paddingLeft }}
      >
        {/* Expand/Collapse for folders */}
        {!isFile ? (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="mr-1 w-5 h-5 flex items-center justify-center flex-shrink-0"
            style={{color: 'rgba(255,255,255,0.4)'}}
          >
            <svg
              className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        ) : (
          <span className="mr-1 w-5 h-5 flex-shrink-0" />
        )}

        {/* Checkbox */}
        <input
          ref={checkboxRef}
          type="checkbox"
          checked={checked}
          onChange={handleToggle}
          className="mr-2 h-4 w-4 rounded cursor-pointer flex-shrink-0"
          style={{borderColor: 'var(--tqa-border-dark)', accentColor: 'var(--tqa-green)'}}
        />

        {/* Icon */}
        {isFile ? (
          <svg className="w-4 h-4 mr-2 flex-shrink-0" style={{color: 'rgba(255,255,255,0.5)'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        ) : (
          <svg className="w-4 h-4 mr-2 text-tqa-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
          </svg>
        )}

        {/* Label */}
        <span
          className={`text-sm truncate ${
            isFile && excluded.has(node.path)
              ? 'line-through opacity-40'
              : isFile
              ? 'opacity-60'
              : 'font-medium'
          }`}
          title={node.path || node.name}
        >
          {node.name}
        </span>

        {/* Folder count badge */}
        {!isFile && totalCount > 0 && (
          <span className="ml-2 text-xs flex-shrink-0" style={{color: 'rgba(255,255,255,0.4)'}}>
            {excludedCount > 0
              ? `${totalCount - excludedCount}/${totalCount}`
              : totalCount}{' '}
            {totalCount === 1 ? 'file' : 'files'}
          </span>
        )}
      </div>

      {/* Children */}
      {!isFile && expanded && node.children && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.path || child.name}
              node={child}
              depth={depth + 1}
              excluded={excluded}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </>
  );
});

TreeNode.displayName = 'TreeNode';

/* ------------------------------------------------------------------ */
/*  Main Modal                                                          */
/* ------------------------------------------------------------------ */
const FileExclusionModal = ({
  isOpen,
  onClose,
  onConfirm,
  fileTree,
  projectName,
  totalFiles,
  loading,
}) => {
  const [excluded, setExcluded] = useState(new Set());
  const [excludeCommentOutActivities, setExcludeCommentOutActivities] = useState(true);
  const [removeArgumentDefaultValues, setRemoveArgumentDefaultValues] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setExcludeCommentOutActivities(true);
      setRemoveArgumentDefaultValues(false);
    }
  }, [isOpen]);

  const handleToggle = useCallback((paths, action) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      paths.forEach((p) => {
        if (action === 'exclude') next.add(p);
        else next.delete(p);
      });
      return next;
    });
  }, []);

  const includedCount = totalFiles - excluded.size;

  const handleSelectAll = useCallback(() => {
    setExcluded(new Set());
  }, []);

  const handleDeselectAll = useCallback(() => {
    if (!fileTree) return;
    const allPaths = collectFilePaths(fileTree);
    setExcluded(new Set(allPaths));
  }, [fileTree]);

  const handleConfirm = useCallback(() => {
    onConfirm({
      excludedFiles: Array.from(excluded),
      excludeCommentOutActivities,
      removeArgumentDefaultValues,
    });
  }, [excluded, excludeCommentOutActivities, removeArgumentDefaultValues, onConfirm]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="rounded-xl shadow-2xl w-[640px] max-w-[95vw] max-h-[85vh] flex flex-col" style={{background: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className="px-6 py-4 flex-shrink-0" style={{borderBottom: '1px solid var(--tqa-border-dark)'}}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold" style={{color: 'rgba(255,255,255,0.85)'}}>Select Files to Include</h2>
              <p className="text-sm mt-0.5" style={{color: 'rgba(255,255,255,0.5)'}}>
                {projectName && (
                  <span className="font-medium" style={{color: 'rgba(255,255,255,0.75)'}}>{projectName}</span>
                )}
                {projectName && ' — '}
                Uncheck files or folders you want to exclude from analysis
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="p-1 rounded-lg transition-colors"
              style={{color: 'rgba(255,255,255,0.4)'}}
              onMouseEnter={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.background = 'transparent'; }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── Toolbar ────────────────────────────────────────────── */}
        <div className="px-6 py-2 flex items-center justify-between flex-shrink-0" style={{borderBottom: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)'}}>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-xs font-medium hover:underline"
              style={{color: 'var(--tqa-green)'}}
            >
              Select All
            </button>
            <span style={{color: 'rgba(255,255,255,0.2)'}}>|</span>
            <button
              type="button"
              onClick={handleDeselectAll}
              className="text-xs font-medium hover:underline"
              style={{color: 'var(--tqa-green)'}}
            >
              Deselect All
            </button>
          </div>
          <div className="text-xs" style={{color: 'rgba(255,255,255,0.5)'}}>
            <span className={`font-semibold ${includedCount === 0 ? 'text-red-500' : 'text-green-600'}`}>
              {includedCount}
            </span>
            {' / '}
            {totalFiles} files selected
            {excluded.size > 0 && (
              <span className="ml-1 text-orange-500">
                ({excluded.size} excluded)
              </span>
            )}
          </div>
        </div>

        {/* ── Tree ───────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0">
          {fileTree ? (
            <TreeNode node={fileTree} depth={0} excluded={excluded} onToggle={handleToggle} />
          ) : (
            <div className="flex items-center justify-center py-12" style={{color: 'rgba(255,255,255,0.4)'}}>
              <svg className="animate-spin w-6 h-6 mr-3" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              Loading project structure…
            </div>
          )}
        </div>

        {/* ── Parsing Options ────────────────────────────────────── */}
        <div className="px-6 py-4 flex-shrink-0" style={{borderTop: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface)'}}>
          <div className="rounded-lg px-4 py-3 space-y-2" style={{background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)'}}>
            <div>
              <h3 className="text-sm font-semibold" style={{color: 'rgba(255,255,255,0.75)'}}>Parsing Options</h3>
              <p className="text-xs mt-0.5" style={{color: 'rgba(255,255,255,0.5)'}}>
                Optional transformations to apply during UiPath parsing.
              </p>
            </div>
            <label className="flex items-start gap-2 text-xs cursor-pointer" style={{color: 'rgba(255,255,255,0.65)'}}>
              <input
                type="checkbox"
                checked={excludeCommentOutActivities}
                onChange={(e) => setExcludeCommentOutActivities(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded" style={{borderColor: 'var(--tqa-border-dark)', accentColor: 'var(--tqa-green)', background: 'transparent'}}
                disabled={loading}
              />
              <span>
                Exclude activities inside <span className="font-medium">CommentOut</span> blocks
              </span>
            </label>
            <label className="flex items-start gap-2 text-xs cursor-pointer" style={{color: 'rgba(255,255,255,0.65)'}}>
              <input
                type="checkbox"
                checked={removeArgumentDefaultValues}
                onChange={(e) => setRemoveArgumentDefaultValues(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded" style={{borderColor: 'var(--tqa-border-dark)', accentColor: 'var(--tqa-green)', background: 'transparent'}}
                disabled={loading}
              />
              <span>
                Remove default values from parsed arguments
              </span>
            </label>
          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────────── */}
        <div className="px-6 py-4 flex items-center justify-between flex-shrink-0 rounded-b-xl" style={{borderTop: '1px solid var(--tqa-border-dark)', background: 'var(--tqa-surface-2)'}}>
          <div className="max-w-sm">
            <p className="text-xs" style={{color: 'rgba(255,255,255,0.4)'}}>
              Excluded files will not be parsed, indexed, or available for AI analysis.
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn-secondary px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading || includedCount === 0}
              className="btn-primary px-5 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Processing…
                </>
              ) : (
                <>
                  Continue with {includedCount} {includedCount === 1 ? 'file' : 'files'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileExclusionModal;
